import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Input,
    Flex,
    Popconfirm,
    Tag,
    Tooltip,
    Modal,
    Form,
    Select,
    Switch,
    InputNumber
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';
import FilterBar from 'components/Common/FilterBar';
import PageActions from 'components/Common/PageActions';
import { DEFAULT_EXPENSE_TYPE, EXPENSE_TYPES, expenseTypeOf } from 'utils/expenseTypes';
import useUrlFilters from 'hooks/useUrlFilters';

const URL_FILTERS = {
    search: { type: 'string' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const { TextArea } = Input;

// Colors accepted by the antd <Tag> used to render categories elsewhere
// (ExpenseForm dropdown, ExpensesTable "CATEGORY" column).
const TAG_COLORS = [
    'default', 'blue', 'purple', 'cyan', 'green', 'magenta',
    'red', 'volcano', 'orange', 'gold', 'lime', 'geekblue'
];

const ExpenseCategoriesTable = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();

    // Search and page live in the URL
    const [filters, setFilters] = useUrlFilters(URL_FILTERS);
    const urlSetter = (name) => (value) => setFilters({ [name]: value });
    const searchTerm = filters.search || '';
    const [searchTermLive, setSearchTermLive] = useState(searchTerm);
    useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

    const currentPage = filters.page;
    const setCurrentPage = urlSetter('page');
    const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
    const setPageSize = urlSetter('pageSize');
    const [totalRows, setTotalRows] = useState(0);

    const [allCategories, setAllCategories] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);

    // Add / edit modal
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchAllCategories();
    }, [searchTerm]);

    useEffect(() => {
        paginateData();
    }, [allCategories, currentPage, pageSize]);

    const fetchAllCategories = async () => {
        setLoading(true);
        try {
            const response = await actions.loadExpenseCategories(searchTerm);
            if (response.status === 200) {
                const data = response.data;
                const results = data.results || data || [];
                setAllCategories(results);
                setTotalRows(results.length);
            }
        } catch (e) {
            console.error('Error fetching expense categories:', e);
        }
        setLoading(false);
    };

    const paginateData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setDataSource(allCategories.slice(startIndex, endIndex));
    };

    const onSearch = (value) => {
        setFilters({ search: value, page: 1 });
    };


    const openAddModal = () => {
        setEditingCategory(null);
        form.resetFields();
        form.setFieldsValue({
            color: 'default',
            expense_type: DEFAULT_EXPENSE_TYPE,
            is_active: true,
            sort_order: allCategories.length + 1,
        });
        setModalOpen(true);
    };

    const openEditModal = (record) => {
        setEditingCategory(record);
        form.setFieldsValue({
            name: record.name,
            code: record.code,
            expense_type: record.expense_type || DEFAULT_EXPENSE_TYPE,
            description: record.description,
            color: record.color || 'default',
            icon: record.icon,
            sort_order: record.sort_order ?? 0,
            is_active: record.is_active,
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingCategory(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            return; // validation errors are shown inline
        }

        const payload = {
            ...values,
            code: values.code?.trim().toUpperCase(),
            description: values.description || null,
            icon: values.icon || null,
        };

        setSaving(true);
        try {
            if (editingCategory) {
                await actions.updateExpenseCategory(payload, editingCategory.id);
            } else {
                await actions.addExpenseCategory(payload);
            }
            closeModal();
            fetchAllCategories();
        } catch (e) {
            // notify() already surfaced the error in the action
        }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        const response = await actions.deleteExpenseCategory(id);
        if (response?.status === 204) {
            fetchAllCategories();
        }
    };

    const handleSeedDefaults = async () => {
        const response = await actions.seedDefaultExpenseCategories();
        if (response?.status === 200) {
            fetchAllCategories();
        }
    };

    const columns = useMemo(() => [
        {
            title: 'NAME',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => (a?.name || '').localeCompare(b?.name || ''),
            width: 220,
            render: (name, record) => (
                <Tag color={record.color || 'default'}>{name}</Tag>
            ),
        },
        {
            title: 'CODE',
            dataIndex: 'code',
            key: 'code',
            sorter: (a, b) => (a?.code || '').localeCompare(b?.code || ''),
            width: 150,
        },
        {
            title: 'EXPENSE TYPE',
            dataIndex: 'expense_type',
            key: 'expense_type',
            width: 140,
            filters: EXPENSE_TYPES.map(({ value, label }) => ({ text: label, value })),
            onFilter: (value, record) => expenseTypeOf(record.expense_type).value === value,
            sorter: (a, b) => expenseTypeOf(a.expense_type).label.localeCompare(expenseTypeOf(b.expense_type).label),
            render: (expenseType) => {
                const { label, color } = expenseTypeOf(expenseType);
                return <Tag color={color}>{label}</Tag>;
            },
        },
        {
            title: 'COLOR',
            dataIndex: 'color',
            key: 'color',
            width: 120,
            render: (color) => color || 'default',
        },
        {
            title: 'ICON',
            dataIndex: 'icon',
            key: 'icon',
            width: 100,
            render: (icon) => icon || '-',
        },
        {
            title: 'ORDER',
            dataIndex: 'sort_order',
            key: 'sort_order',
            width: 90,
            align: 'right',
            sorter: (a, b) => (a?.sort_order || 0) - (b?.sort_order || 0),
        },
        {
            title: 'ACTIVE',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 90,
            align: 'center',
            render: (isActive) => (
                isActive ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
                ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>No</Tag>
                )
            ),
        },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            width: 280,
            ellipsis: {
                showTitle: false,
            },
            render: (desc) => desc ? (
                <Tooltip placement="topLeft" title={desc}>{desc}</Tooltip>
            ) : '-',
        },
        {
            title: 'ACTIONS',
            dataIndex: 'actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Flex gap="small">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete category?"
                        description="Expenses already using this category will keep it."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Delete">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Flex>
            ),
        },
    ], [allCategories]);

    return (
        <div style={{ padding: 10 }}>
            <div style={{ marginBottom: 8 }}>
              Showing {totalRows === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} categories
      </div>
            <Flex gap="large" justify="space-between" align="flex-start" wrap>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
                        searchValue={searchTermLive}
                        onSearchChange={setSearchTermLive}
                        onSearch={onSearch}
                        searchPlaceholder="Search categories"
                        onRefresh={() => fetchAllCategories()}
                        refreshing={loading}
                    />
                </div>
                <PageActions
                    primary={{
                        label: 'Add Category',
                        shortLabel: 'Add',
                        icon: <PlusOutlined />,
                        onClick: openAddModal,
                    }}
                    actions={[
                        {
                            key: 'seed',
                            label: 'Seed Defaults',
                            icon: <PlusOutlined />,
                            // Was a Popconfirm on the button; a menu item cannot
                            // host one, so confirm in a modal instead.
                            onClick: () => Modal.confirm({
                                title: 'Add the default categories?',
                                content: 'Creates any of the standard set that is missing. Existing categories are left untouched.',
                                onOk: handleSeedDefaults,
                            }),
                        },
                        {
                            key: 'back',
                            label: 'Back to Expenses',
                            icon: <ArrowLeftOutlined />,
                            onClick: () => navigate('/expenses'),
                        },
                    ]}
                />
            </Flex>

            <div style={{ marginTop: 20 }}>
                <Table
                    loading={loading}
                    bordered
                    size="small"
                    dataSource={dataSource}
                    columns={columns}
                    rowKey={row => row.id}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRows,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        pageSizeOptions: ['10', '25', '50', '100'],
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            if (size !== pageSize) {
                                setPageSize(size);
                                setCurrentPage(1);
                            }
                        },
                    }}
                    scroll={{
                        x: 'max-content',
                        y: 'calc(100vh - 350px)',
                    }}
                    sticky
                />
            </div>

            <Modal
                title={editingCategory ? 'Edit Expense Category' : 'Add Expense Category'}
                open={modalOpen}
                onCancel={closeModal}
                onOk={handleSubmit}
                okText={editingCategory ? 'Update' : 'Create'}
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter a name' }]}
                    >
                        <Input placeholder="e.g. Travel & Transport" maxLength={100} />
                    </Form.Item>

                    <Form.Item
                        label="Code"
                        name="code"
                        tooltip="Unique identifier used by the API and imports (e.g. TRAVEL). Cannot clash with an existing category."
                        rules={[
                            { required: true, message: 'Please enter a code' },
                            {
                                pattern: /^[A-Za-z0-9_]+$/,
                                message: 'Use letters, numbers and underscores only',
                            },
                        ]}
                        normalize={(value) => value?.toUpperCase()}
                    >
                        <Input placeholder="e.g. TRAVEL" maxLength={50} />
                    </Form.Item>

                    <Form.Item
                        label="Expense Type"
                        name="expense_type"
                        tooltip="Variable expenses move with business activity (ads, commissions). Non-variable expenses stay the same each month (rent, salaries)."
                        rules={[{ required: true, message: 'Please select an expense type' }]}
                    >
                        <Select
                            options={EXPENSE_TYPES.map(({ value, label, color }) => ({
                                value,
                                label: <Tag color={color}>{label}</Tag>,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item label="Description" name="description">
                        <TextArea rows={2} placeholder="Optional description" />
                    </Form.Item>

                    <Flex gap="middle">
                        <Form.Item label="Color" name="color" style={{ flex: 1 }}>
                            <Select
                                options={TAG_COLORS.map((color) => ({
                                    value: color,
                                    label: <Tag color={color}>{color}</Tag>,
                                }))}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Sort Order"
                            name="sort_order"
                            tooltip="Lower numbers appear first in the expense category dropdown."
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Flex>

                    <Flex gap="middle">
                        <Form.Item
                            label="Icon"
                            name="icon"
                            tooltip="Optional icon name, stored for future UI use."
                            style={{ flex: 1 }}
                        >
                            <Input placeholder="Optional" maxLength={50} />
                        </Form.Item>

                        <Form.Item
                            label="Active"
                            name="is_active"
                            valuePropName="checked"
                            tooltip="Inactive categories are hidden from the expense form dropdown."
                            style={{ flex: 1 }}
                        >
                            <Switch checkedChildren="Yes" unCheckedChildren="No" />
                        </Form.Item>
                    </Flex>
                </Form>
            </Modal>
        </div>
    );
};

export default ExpenseCategoriesTable;
